import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, DollarSign, Users, Truck, Package, FolderOpen, FileText, Wrench } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";

const ExecutiveDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [projectStats, setProjectStats] = useState([]);
  const [costData, setCostData] = useState([]);
  const [equipmentStats, setEquipmentStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  const COLORS = ['#1e3a5f', '#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashRes, projectRes, costRes, equipRes, approvalRes] = await Promise.all([
        apiClient.get('/dashboard/overview'),
        apiClient.get('/dashboard/project-stats'),
        apiClient.get('/dashboard/cost-analytics'),
        apiClient.get('/dashboard/equipment-utilization'),
        apiClient.get('/dashboard/pending-approvals')
      ]);
      setDashboardData(dashRes.data);
      setProjectStats(projectRes.data);
      setCostData(costRes.data);
      setEquipmentStats(equipRes.data);
      setPendingApprovals(approvalRes.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${value?.toLocaleString() || 0}`;
  };

  if (loading) return <div className="text-center py-12">Loading dashboard...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">EXECUTIVE DASHBOARD</h1>
          <p className="text-slate-600 mt-2 font-body">Real-time overview of all operations</p>
        </div>
        <button onClick={fetchDashboardData} className="btn-outline">Refresh Data</button>
      </div>

      {/* KPI Cards */}
      <div className="grid md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <FolderOpen className="w-8 h-8 text-primary" />
            <span className="text-xs text-slate-500">Projects</span>
          </div>
          <p className="font-heading font-black text-3xl text-primary mt-2">{dashboardData?.total_projects || 0}</p>
          <p className="text-xs text-slate-600">Active: {dashboardData?.active_projects || 0}</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <DollarSign className="w-8 h-8 text-accent" />
            <span className="text-xs text-slate-500">Total Budget</span>
          </div>
          <p className="font-heading font-black text-2xl text-accent mt-2">{formatCurrency(dashboardData?.total_budget)}</p>
          <p className="text-xs text-slate-600">Spent: {formatCurrency(dashboardData?.total_spent)}</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <Users className="w-8 h-8 text-success" />
            <span className="text-xs text-slate-500">Workforce</span>
          </div>
          <p className="font-heading font-black text-3xl text-success mt-2">{dashboardData?.total_employees || 0}</p>
          <p className="text-xs text-slate-600">Present Today: {dashboardData?.present_today || 0}</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <Truck className="w-8 h-8 text-warning" />
            <span className="text-xs text-slate-500">Equipment</span>
          </div>
          <p className="font-heading font-black text-3xl text-warning mt-2">{dashboardData?.total_equipment || 0}</p>
          <p className="text-xs text-slate-600">Active: {dashboardData?.active_equipment || 0}</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <Package className="w-8 h-8 text-primary" />
            <span className="text-xs text-slate-500">Materials</span>
          </div>
          <p className="font-heading font-black text-3xl text-primary mt-2">{dashboardData?.low_stock_items || 0}</p>
          <p className="text-xs text-error">Low Stock Items</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <AlertTriangle className="w-8 h-8 text-error" />
            <span className="text-xs text-slate-500">Pending</span>
          </div>
          <p className="font-heading font-black text-3xl text-error mt-2">{dashboardData?.pending_approvals || 0}</p>
          <p className="text-xs text-slate-600">Approvals Required</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Project Progress */}
        <div className="stat-card">
          <h3 className="font-heading font-bold text-lg mb-4">PROJECT PROGRESS</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={projectStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{fontSize: 10}} angle={-45} textAnchor="end" height={80} />
              <YAxis domain={[0, 100]} />
              <Tooltip formatter={(value) => `${value}%`} />
              <Bar dataKey="progress" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Budget vs Actual */}
        <div className="stat-card">
          <h3 className="font-heading font-bold text-lg mb-4">BUDGET VS ACTUAL SPEND</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={costData?.projects || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{fontSize: 10}} angle={-45} textAnchor="end" height={80} />
              <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="budget" fill="#1e3a5f" name="Budget" radius={[4, 4, 0, 0]} />
              <Bar dataKey="spent" fill="#f59e0b" name="Spent" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Cost Overrun Alerts */}
        <div className="stat-card">
          <h3 className="font-heading font-bold text-lg mb-4">COST ALERTS</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {costData?.alerts?.length > 0 ? costData.alerts.map((alert, idx) => (
              <div key={idx} className={`p-3 rounded-sm border-l-4 ${alert.severity === 'high' ? 'bg-red-50 border-l-error' : 'bg-yellow-50 border-l-warning'}`}>
                <p className="font-medium text-sm">{alert.project}</p>
                <p className="text-xs text-slate-600">{alert.message}</p>
                <p className={`text-xs font-bold ${alert.severity === 'high' ? 'text-error' : 'text-warning'}`}>
                  {alert.variance > 0 ? '+' : ''}{alert.variance}% variance
                </p>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-success" />
                <p>No cost alerts</p>
              </div>
            )}
          </div>
        </div>

        {/* Equipment Utilization */}
        <div className="stat-card">
          <h3 className="font-heading font-bold text-lg mb-4">EQUIPMENT UTILIZATION</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-3 bg-slate-50 rounded-sm">
              <p className="text-2xl font-bold text-primary">{equipmentStats?.avg_utilization || 0}%</p>
              <p className="text-xs text-slate-600">Avg Utilization</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-sm">
              <p className="text-2xl font-bold text-warning">{equipmentStats?.idle_equipment || 0}</p>
              <p className="text-xs text-slate-600">Idle Today</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie
                data={equipmentStats?.status_breakdown || []}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={60}
                paddingAngle={5}
                dataKey="value"
              >
                {(equipmentStats?.status_breakdown || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Pending Approvals */}
        <div className="stat-card">
          <h3 className="font-heading font-bold text-lg mb-4">PENDING APPROVALS</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendingApprovals?.length > 0 ? pendingApprovals.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                <div>
                  <p className="font-medium text-sm">{item.type}</p>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-warning" />
                  <span className="text-xs text-slate-500">{item.days_pending}d</span>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-success" />
                <p>All caught up!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fuel Efficiency */}
      <div className="stat-card">
        <h3 className="font-heading font-bold text-lg mb-4">FUEL CONSUMPTION TREND (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={equipmentStats?.fuel_trend || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{fontSize: 10}} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="consumption" stroke="#f59e0b" strokeWidth={2} name="Fuel (Ltr)" />
            <Line type="monotone" dataKey="hours" stroke="#1e3a5f" strokeWidth={2} name="Running Hours" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
